/**
 * Version card + its row-list wrapper, extracted from version-history-tab.tsx
 * to keep that file under the max-lines lint limit.
 */
import * as React from 'react';
import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Layers, Banknote, Eye, XCircle, Clock, Wallet } from 'lucide-react';
import { Badge } from '@ooh/ui/components/badge';
import { formatDisplayDate } from '@ooh/ui/lib/date-format';
import { offerVersionEndpoints } from '@ooh/contracts';
import type { OfferVersion, OfferVersionStatus } from '@ooh/contracts';
import { useApiSuspenseQuery } from '../../data/use-api-suspense-query';
import { ErrorBoundary } from '../../layouts/error-boundary';
import { Link } from '../../plugs/link';
import { unitDecisionsRequest } from './api/queries';
import { computeSummary } from './unit-decisions/filters';
import { formatOfferedPrice } from './unit-decisions/format-helpers';
import {
  getVersionWithdrawAction,
  VersionRowActions,
  type WithdrawAcceptanceMode,
} from './version-row-actions';
import { isClientActedVersion } from './lib/offer-decision-version';

// Non-user-facing selectors retained for existing tests and automation.
const PREVIEW_TEST_ID = 'offer-version-preview-btn';
const CLIENT_SHARE_TEST_ID = 'version-row-client-share-btn';

// ─── Helpers ──────────────────────────────────────────────────

function statusBadgeVariant(status: OfferVersionStatus): 'info' | 'success' | 'outline' {
  if (status === 'PUBLISHED') return 'info';
  if (status === 'ACCEPTED') return 'success';
  return 'outline';
}

// ─── Version row meta ────────────────────────────────────────

function VersionMeta({ version, isNewest }: { version: OfferVersion; isNewest: boolean }) {
  const { t } = useTranslation('offers');

  return (
    <div className="min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <Badge variant={statusBadgeVariant(version.status)}>
          {t(`workspace.versions.statusLabel.${version.status}`)}
        </Badge>
        {version.rejectedAt != null ? (
          <Badge variant="destructive" data-testid="version-rejected-badge">
            {t('workspace.versions.rejectedBadge')}
          </Badge>
        ) : null}
        {isNewest ? (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-primary bg-primary/8 px-2 py-0.5 rounded-full border border-primary/20">
            <CheckCircle2 className="h-3 w-3" />
            {t('workspace.versions.activeVersionLabel')}
          </span>
        ) : null}
      </div>

      <p className="text-sm text-muted-foreground mt-1">
        {formatDisplayDate(version.publishedAt)}{' '}
        {version.publishedByName
          ? t('workspace.versions.publishedByName', { name: version.publishedByName })
          : t('workspace.versions.publishedBy', { id: version.publishedBy })}
      </p>

      {version.changeDescription ? (
        <p className="text-sm text-foreground mt-1.5 leading-relaxed line-clamp-2">
          {version.changeDescription}
        </p>
      ) : null}
    </div>
  );
}

// ─── Client decision summary (CU-86caypxdz) ────────────────────
// A compact "what did the client decide" strip shown on a version card once
// it's been shared and the client has acted (accepted or rejected) — QA
// (07/29 session) reported the Wersje tab still showing pre-send unit/price
// totals after the client's decision, with the breakdown visible only in
// the separate "Decyzje klienta" tab. Reuses the same computeSummary()/
// useUnitDecisions() pair that tab already uses, scoped to this version.

/** Wraps the client-acted gate so VersionRow's render stays a flat branch (no nested ternary). */
function VersionDecisionSummaryGate({
  version,
  offerId,
}: {
  version: OfferVersion;
  offerId: string;
}) {
  if (!isClientActedVersion(version)) return null;
  return (
    <ErrorBoundary onReset={() => undefined} renderError={() => null}>
      <Suspense fallback={null}>
        <VersionDecisionSummary offerId={offerId} versionId={version.id} />
      </Suspense>
    </ErrorBoundary>
  );
}

function VersionDecisionSummary({ offerId, versionId }: { offerId: string; versionId: number }) {
  const { t } = useTranslation('offers');
  const { data } = useApiSuspenseQuery(
    offerVersionEndpoints.unitDecisions,
    unitDecisionsRequest(offerId, versionId),
  );

  const summary = computeSummary(data);
  if (summary.total === 0) return null;

  return (
    <div
      className="mt-3 flex flex-wrap items-center gap-4 text-xs text-muted-foreground"
      data-testid="version-decision-summary"
    >
      <span className="inline-flex items-center gap-1 text-emerald-700">
        <CheckCircle2 className="h-3.5 w-3.5" />
        {t('workspace.versions.unitDecisions.summary.acceptedLabel')}: {summary.accepted}
      </span>
      <span className="inline-flex items-center gap-1 text-red-700">
        <XCircle className="h-3.5 w-3.5" />
        {t('workspace.versions.unitDecisions.summary.rejectedLabel')}: {summary.rejected}
      </span>
      <span className="inline-flex items-center gap-1">
        <Clock className="h-3.5 w-3.5" />
        {t('workspace.versions.unitDecisions.summary.undecidedLabel')}: {summary.undecided}
      </span>
      <span className="inline-flex items-center gap-1">
        <Wallet className="h-3.5 w-3.5" />
        {t('workspace.versions.unitDecisions.summary.acceptedValueLabel')}:{' '}
        {formatOfferedPrice(summary.acceptedValuePln)}
      </span>
    </div>
  );
}

// ─── Version row ─────────────────────────────────────────────

function VersionRow({
  version,
  isNewest,
  offerId,
  hasPriorVersion,
  onCompareClick,
  canEdit,
  onWithdrawClick,
  onOpenClientShare,
  onOpenVersion,
}: {
  version: OfferVersion;
  isNewest: boolean;
  offerId: string;
  hasPriorVersion: boolean;
  onCompareClick: (versionId: number) => void;
  /** Gates "Wycofaj akceptację" / "Zarejestruj odrzucenie" — same offer edit-access
   * check as the tab's other owner-only actions (offer-versioning-diff / Internal
   * acceptance-withdrawal control on the Versions tab). */
  canEdit: boolean;
  onWithdrawClick: (versionId: number, mode: WithdrawAcceptanceMode) => void;
  /** Only wired for the newest version's row — see CU-86caypxpc. */
  onOpenClientShare?: () => void;
  onOpenVersion: (versionId: number) => void;
}) {
  const { t } = useTranslation('offers');

  const handleCardClick = React.useCallback(() => {
    onOpenVersion(version.id);
  }, [onOpenVersion, version.id]);
  const withdrawAction = getVersionWithdrawAction({
    versionId: version.id,
    status: version.status,
    canEdit,
    withdrawLabel: t('workspace.versions.withdrawAcceptance.withdrawButton'),
    registerRejectionLabel: t('workspace.versions.withdrawAcceptance.registerRejectionButton'),
    onWithdrawClick,
  });

  return (
    <div className="flex flex-col" data-testid={`version-card-v${version.versionNumber}`}>
      <div
        role="link"
        tabIndex={-1}
        className={`border border-border rounded-xl p-5 bg-card transition-shadow hover:shadow-sm cursor-pointer ${
          isNewest ? 'ring-1 ring-primary/20 border-primary/30' : ''
        }`}
        data-testid="offer-version-row"
        onClick={handleCardClick}
      >
        {/* Top row */}
        <div className="flex items-start justify-between gap-4">
          {/* Version badge + meta */}
          <div className="flex items-start gap-4">
            {/* Version number circle */}
            <div className="flex-none w-12 h-12 rounded-full bg-primary/8 flex items-center justify-center border border-primary/20">
              <span className="text-lg font-bold text-primary leading-none">
                {t('workspace.versions.versionBadge', { number: version.versionNumber })}
              </span>
            </div>

            <VersionMeta version={version} isNewest={isNewest} />
          </div>
        </div>

        {/* Summary stats */}
        <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <Layers className="h-3.5 w-3.5" />
            {t('workspace.versions.unitCount', { count: version.unitCount })}
          </span>
          {version.totalNetPrice != null ? (
            <span className="inline-flex items-center gap-1">
              <Banknote className="h-3.5 w-3.5" />
              {`${version.totalNetPrice.toLocaleString('pl-PL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} zł`}
            </span>
          ) : null}
        </div>

        {/* Client decision summary — only once the client has acted on this version */}
        <VersionDecisionSummaryGate version={version} offerId={offerId} />

        <VersionRowActions
          actionsLabel={t('workspace.versions.actionsLabel')}
          preview={{
            label: t('workspace.versions.previewTooltip'),
            testId: PREVIEW_TEST_ID,
            link: (
              <Link to={`/offers/${offerId}/versions/${version.id}`}>
                <Eye className="h-4 w-4" />
              </Link>
            ),
          }}
          clientShare={
            isNewest && onOpenClientShare
              ? {
                  label: t('workspace.header.clientShare.trigger'),
                  onClick: onOpenClientShare,
                  testId: CLIENT_SHARE_TEST_ID,
                }
              : undefined
          }
          compare={
            hasPriorVersion
              ? {
                  label: t('workspace.versions.diff.compareBtn'),
                  onClick: () => onCompareClick(version.id),
                  testId: `compare-btn-v${version.versionNumber}`,
                }
              : undefined
          }
          withdraw={withdrawAction}
        />
      </div>
    </div>
  );
}

// ─── Version row list ────────────────────────────────────────
// Extracted from VersionHistoryTab to keep that component's cyclomatic
// complexity within the lint budget.

export function VersionRowList({
  sortedNewestFirst,
  offerId,
  onCompareClick,
  canEdit,
  onWithdrawClick,
  onOpenClientShare,
  onOpenVersion,
}: {
  sortedNewestFirst: OfferVersion[];
  offerId: string;
  onCompareClick: (versionId: number) => void;
  canEdit: boolean;
  onWithdrawClick: (versionId: number, mode: WithdrawAcceptanceMode) => void;
  onOpenClientShare?: () => void;
  onOpenVersion: (versionId: number) => void;
}) {
  return (
    <div className="flex-1 overflow-y-auto px-6 py-5">
      <div className="max-w-2xl mx-auto flex flex-col gap-4">
        {sortedNewestFirst.map((version, index) => {
          // The version immediately older than current in ascending API order —
          // sortedNewestFirst[index+1] is the next-older version.
          const hasPriorVersion = sortedNewestFirst[index + 1] != null;
          return (
            <VersionRow
              key={version.id}
              version={version}
              isNewest={index === 0}
              offerId={offerId}
              hasPriorVersion={hasPriorVersion}
              onCompareClick={onCompareClick}
              canEdit={canEdit}
              onWithdrawClick={onWithdrawClick}
              onOpenClientShare={index === 0 ? onOpenClientShare : undefined}
              onOpenVersion={onOpenVersion}
            />
          );
        })}
      </div>
    </div>
  );
}
